/**
 * The season recap (docs/native-parity.md "Season recap"): the household's month cut as a video,
 * shown once as an interstitial on the next open and replayed from the Peon of the Month card.
 * Everything here is plain logic and a tiny store, so Node tests can import it; the layers are
 * src/components/Recap.tsx.
 */

import type { BoardSummary, RecapRef, RecapSummary } from "../shared/types";

/**
 * The server's recap row (GET /api/recaps/:id, BoardSummary.recap), and the recap as a Hall month
 * carries it and the player takes it (HallMonth.recap), from shared/types.ts.
 */
export type { RecapRef, RecapSummary };

/** A board summary as far as the recap goes: one remembered before the field existed has none. */
export type WithRecap = Pick<BoardSummary, "recap">;

/** The recap a summary carries, or null (absent on an older summary, null with none). */
export function recapOf(summary: WithRecap | null | undefined): RecapSummary | null {
  return summary?.recap ?? null;
}

/** The device's own mark that this recap was seen or skipped here. */
export const RECAP_SEEN_PREFIX = "op.recapSeen.";
export function recapSeenKey(id: string): string {
  return `${RECAP_SEEN_PREFIX}${id}`;
}

/**
 * Whether the interstitial shows for this summary: the household's `recapUI` flag is on (owner: "so
 * it won't appear till I've finalized the video"), and the summary carries a recap the server says
 * this peon has not seen, with no mark on this device. Either side saying seen is enough to stay quiet.
 */
export function shouldOfferRecap(enabled: boolean, summary: WithRecap | null | undefined, seenHere: (id: string) => boolean): RecapSummary | null {
  if (!enabled) return null;
  const recap = recapOf(summary);
  if (!recap || recap.seen) return null;
  if (seenHere(recap.id)) return null;
  return recap;
}

/** The long side of the viewport decides the cut: 1080 px or more plays hd, anything less sd. */
export const HD_LONG_SIDE = 1080;
export type RecapQuality = "sd" | "hd";
export function recapQuality(width: number, height: number): RecapQuality {
  return Math.max(width, height) >= HD_LONG_SIDE ? "hd" : "sd";
}

/**
 * The cookie paths, still served to the household's own session when the household flag is on (a
 * URL typed in by hand). The app itself loads signed links instead (recapLink below).
 */
export function recapVideoUrl(id: string, q: RecapQuality): string {
  return `/api/recaps/${encodeURIComponent(id)}/video?q=${q}`;
}
export function recapPosterUrl(id: string): string {
  return `/api/recaps/${encodeURIComponent(id)}/poster`;
}

// ---------- signed links ----------
//
// The player and the poster load through GET /api/recaps/:id/link: a <video> or <img> cannot send
// x-flag-preview, so a device previewing the recap with its own switch would get a 404 on the cookie
// path, while a valid signature opens the bytes on its own. Links are kept per id and part, reused
// while they have more than LINK_MARGIN_MS left, and asked for once at a time.

export type RecapPart = RecapQuality | "poster";
export type RecapLinkFetch = (id: string, part: RecapPart) => Promise<{ url: string; expiresAt: string }>;

/** A link this close to expiring is asked for again, so a paused video does not lose its source. */
export const LINK_MARGIN_MS = 30 * 60 * 1000;

const links = new Map<string, { url: string; expiresMs: number }>();
const pendingLinks = new Map<string, Promise<string>>();

/** A kept signed link with more than LINK_MARGIN_MS left, or null. */
export function cachedRecapLink(id: string, part: RecapPart, nowMs = Date.now()): string | null {
  const hit = links.get(`${id}:${part}`);
  return hit && hit.expiresMs - nowMs > LINK_MARGIN_MS ? hit.url : null;
}

/** The signed link for `part` of recap `id`: the kept one when fresh, else one asked of the server. */
export function recapLink(id: string, part: RecapPart, fetchLink: RecapLinkFetch): Promise<string> {
  const cached = cachedRecapLink(id, part);
  if (cached) return Promise.resolve(cached);
  const key = `${id}:${part}`;
  const pending = pendingLinks.get(key);
  if (pending) return pending;
  const next = fetchLink(id, part)
    .then(({ url, expiresAt }) => {
      const expiresMs = Date.parse(expiresAt);
      links.set(key, { url, expiresMs: Number.isNaN(expiresMs) ? 0 : expiresMs });
      return url;
    })
    .finally(() => pendingLinks.delete(key));
  pendingLinks.set(key, next);
  return next;
}

/** Asks for the poster's and this viewport's video link ahead of the tap, so the player starts with its source. */
export function warmRecapLinks(id: string, quality: RecapQuality, fetchLink: RecapLinkFetch): void {
  void recapLink(id, "poster", fetchLink).catch(() => {});
  void recapLink(id, quality, fetchLink).catch(() => {});
}

/** "4 min 52 s"; under a minute "38 s"; on the minute "5 min". */
export function formatRecapDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s} s`;
  return s === 0 ? `${m} min` : `${m} min ${s} s`;
}

/** The replay control's name for a screen reader: the verb and which recap. */
export function watchRecapLabel(recap: Pick<RecapSummary, "title">): string {
  return `Watch recap, ${recap.title}`;
}

/**
 * How the player turns the phone sideways (docs/native-parity.md "Season recap"):
 * - "element": the Fullscreen API on the player layer, then screen.orientation.lock("landscape")
 *   (Android Chrome honours the lock; a desktop ignores it);
 * - "video": iPhone Safari has no element fullscreen, so the video's own webkitEnterFullscreen()
 *   hands it to the system player, which turns with the phone;
 * - "none": neither; the layer plays inline with its Close.
 */
export type LandscapeRoute = "element" | "video" | "none";
export function landscapeRoute(env: { elementFullscreen: boolean; videoFullscreen: boolean }): LandscapeRoute {
  if (env.elementFullscreen) return "element";
  if (env.videoFullscreen) return "video";
  return "none";
}

// ---------- the device mark ----------

export function isRecapSeenHere(id: string): boolean {
  try {
    return localStorage.getItem(recapSeenKey(id)) === "1";
  } catch {
    return false;
  }
}

export function markRecapSeenHere(id: string): void {
  try {
    localStorage.setItem(recapSeenKey(id), "1");
  } catch {
    /* private mode: the server's record still keeps it from showing again */
  }
}

// ---------- the store: what the app layer shows ----------

export interface RecapLayers {
  /** The interstitial's recap, while it waits to be answered. */
  offer: RecapSummary | null;
  /** The recap playing in the player layer: the newest from the crown card, or any month's from the Hall. */
  playing: RecapRef | null;
}

let state: RecapLayers = { offer: null, playing: null };
/** At most one interstitial per app open (a page load). */
let offeredThisOpen = false;
const listeners = new Set<(s: RecapLayers) => void>();

function set(next: Partial<RecapLayers>): void {
  state = { ...state, ...next };
  for (const fn of listeners) fn(state);
}

export function recapLayers(): RecapLayers {
  return state;
}

export function subscribeRecap(fn: (s: RecapLayers) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Whether the crown card offers the replay: the flag on and the recap is the crowned month's. */
export function showsRecapControl(enabled: boolean, recap: RecapSummary | null | undefined): recap is RecapSummary {
  return enabled && !!recap && recap.forCrown;
}

/**
 * A board summary arrived from the server (either tab): queue the interstitial when it should show
 * and nothing has been offered this open. Returns whether it queued one.
 */
export function offerRecap(enabled: boolean, summary: WithRecap | null | undefined, seenHere: (id: string) => boolean = isRecapSeenHere): boolean {
  if (offeredThisOpen) return false;
  const recap = shouldOfferRecap(enabled, summary, seenHere);
  if (!recap) return false;
  offeredThisOpen = true;
  set({ offer: recap });
  return true;
}

export function dismissRecapOffer(): void {
  if (state.offer) set({ offer: null });
}

export function playRecap(recap: RecapRef): void {
  set({ offer: null, playing: recap });
}

export function stopRecap(): void {
  if (state.playing) set({ playing: null });
}

/** For tests: back to a fresh app open. */
export function resetRecapForTests(): void {
  offeredThisOpen = false;
  links.clear();
  pendingLinks.clear();
  state = { offer: null, playing: null };
}
